// Destiny 2 stores its key bindings in a local cvars.xml (at %AppData%/Roaming/Bungie/DestinyPC/prefs)
// - a real export, NOT cloud-only (retiring the stale "cloud-only, no export file" premise). This
// module recovers the bindings: parseCvars reads the file's `key bindings` namespace, and
// destinyAlternates translates each cvar value into the token vocabulary the projector reads.
// Curation (which cvars to keep, their Command names/categories/Contexts) lives in the
// Destiny ingest; this stays a pure, lens-independent translation, the Destiny analogue of the SC2
// parser.

/** A translated alternate: a Keylens token plus the modifier combo it is held under. */
export interface DestinyAlternate {
  token: string;
  mods: { ctrl: boolean; shift: boolean; alt: boolean };
}

/**
 * Destiny's raw input spellings to Keylens tokens. The multi-word mouse / wheel / extra-button names
 * and the positional names Destiny spells out; single characters fall through to the char path
 * (letters upper-cased, the legend convention). A bare "control" / "shift" / "alt" here is a
 * modifier bound AS A KEY (FPS games do this - jump = control), which the projector lands on the key
 * whose active-layer keycode is that modifier.
 */
const INPUT_TOKEN: Record<string, string> = {
  "left mouse button": "MouseLeft",
  "right mouse button": "MouseRight",
  "middle mouse button": "MouseMiddle",
  "mouse wheel up": "WheelUp",
  "mouse wheel down": "WheelDown",
  "extra mouse button 1": "Mouse4",
  "extra mouse button 2": "Mouse5",
  control: "Ctrl",
  shift: "Shift",
  alt: "Alt",
  tab: "Tab",
  space: "Space",
  enter: "Enter",
  // navigation cluster: positional keys, identical in every Keyboard Layout. "page up" is verified
  // from a real export (the maintainer's cvars); the rest follow Destiny's same lowercase spelling.
  "page up": "PageUp",
  "page down": "PageDown",
  home: "Home",
  end: "End",
  insert: "Insert",
  delete: "Delete",
  // arrow keys (Destiny's default emotes bind these): positional tokens the projector passes through
  up: "ArrowUp",
  down: "ArrowDown",
  left: "ArrowLeft",
  right: "ArrowRight",
};

/**
 * The token a single raw Destiny input denotes, or null when it has no place on the board: "escape"
 * (Destiny's filler for an empty secondary slot), "print screen", and any unknown multi-word name.
 * Dropping these here mirrors how the SC2 parser filters tokens it cannot map.
 */
/** Destiny's lowercase function-key spelling, "f1".."f12" (verified f2 / f10 from a real export). */
const FKEY = /^f([1-9]|1[0-2])$/;

function inputToken(raw: string): string | null {
  const mapped = INPUT_TOKEN[raw];
  if (mapped) return mapped;
  if (raw.length === 1) return /[a-z]/i.test(raw) ? raw.toUpperCase() : raw;
  // function keys: the token is the upper-case "F1".."F12" the projector recognises positionally
  const fkey = FKEY.exec(raw);
  if (fkey) return `F${fkey[1]}`;
  // Not yet mapped, pending a real export to confirm Destiny's spelling: the numpad cluster (numpad
  // digits / operators / Enter / decimal), Backspace, and Caps Lock. The projector already has codes
  // for all of them (TOKEN_TO_CODE / zones), so each only needs its raw Destiny spelling added to
  // INPUT_TOKEN once observed. Audited 2026-06-18 against the stock-default + maintainer's live cvars:
  // no bound key drops except "print screen" (intentional, no board place). Lock / Pause keys have no
  // board purpose and stay dropped.
  return null;
}

/**
 * Split a Destiny cvar value into its renderable alternates. A value is `primary!secondary` (the two
 * bind slots), each `mod+...+key` with the modifiers named control / shift / alt. An "unused" slot
 * and any alternate whose key has no board token (the escape filler, print screen) drop, so every
 * returned alternate places on a key - the same net effect the SC2 parser has filtering unmappable
 * tokens. No real Destiny input string contains "+" except a held-modifier combo, so the split is safe.
 */
export function destinyAlternates(value: string): DestinyAlternate[] {
  const out: DestinyAlternate[] = [];
  for (const slot of value.split("!")) {
    const s = slot.trim();
    if (!s || s === "unused") continue;
    const parts = s.split("+").map((p) => p.trim());
    const key = parts[parts.length - 1] ?? "";
    const token = inputToken(key);
    if (token == null) continue;
    const mods = { ctrl: false, shift: false, alt: false };
    for (const p of parts.slice(0, -1)) {
      if (p === "control") mods.ctrl = true;
      else if (p === "shift") mods.shift = true;
      else if (p === "alt") mods.alt = true;
    }
    out.push({ token, mods });
  }
  return out;
}

/** The cvars.xml marker the parser keys on - the namespace that holds the key bindings. */
const CVARS_MARKER = /<namespace\s+name="key bindings">/i;

/**
 * Cheap sniff for the import pipeline: the `key bindings` namespace only a Destiny 2 cvars.xml
 * carries (the parser reads the same marker). The Destiny analogue of looksLikeSC2Hotkeys, so the
 * dispatcher can route a dropped cvars file to the Destiny ingest without the user choosing a type.
 */
export function looksLikeCvars(text: string): boolean {
  return CVARS_MARKER.test(text);
}

/**
 * Parse a Destiny 2 cvars.xml into its key-binding cvars: `{ cvarName: rawValue }`. Reads ONLY the
 * `key bindings` namespace (the file also carries graphics / audio / gameplay sections). A tolerant
 * scan keyed to the fixed `<cvar name=".." value=".." />` shape Destiny machine-writes - not a
 * general XML parser, and none is needed for this stable, flat format.
 */
export function parseCvars(xml: string): Record<string, string> {
  const out: Record<string, string> = {};
  const ns = /<namespace\s+name="key bindings">([\s\S]*?)<\/namespace>/.exec(xml);
  if (!ns?.[1]) return out;
  for (const m of ns[1].matchAll(/<cvar\s+name="([^"]*)"\s+value="([^"]*)"\s*\/>/g)) {
    const name = m[1];
    const value = m[2];
    if (name != null && value != null) out[name] = value;
  }
  return out;
}
